import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { formatINR } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { StatCard, PageHeader } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { Wallet, Lock, ArrowDownToLine, ArrowUpFromLine, Clock, TrendingUp, TrendingDown } from "lucide-react";
import { Loader2 } from "lucide-react";

export default function UserDashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => {
    const load = () => api.get("/user/dashboard").then((r) => setD(r.data)).catch(() => {});
    load();
    window.addEventListener("vp:payment-credited", load);
    return () => window.removeEventListener("vp:payment-credited", load);
  }, []);

  if (!d) return <div className="grid place-items-center py-32"><Loader2 className="h-8 w-8 animate-spin text-emerald-400" /></div>;

  return (
    <div>
      <PageHeader title={`Hi, ${user?.name?.split(" ")[0] || "there"} 👋`} desc="Here's your wallet at a glance." />

      <div className="vp-card vp-glow rounded-2xl p-6 sm:p-8 mb-6 relative overflow-hidden">
        <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-emerald-500/10 blur-3xl" />
        <div className="text-xs font-mono uppercase tracking-widest text-slate-400">Available Balance</div>
        <div className="mt-2 font-display text-4xl sm:text-5xl font-extrabold text-emerald-400" data-testid="user-balance">{formatINR(d.available_balance)}</div>
        <div className="mt-2 text-sm text-slate-400 font-mono flex items-center gap-2"><Lock className="h-3.5 w-3.5 text-amber-400" /> Reserved for pending payouts: <span className="text-amber-300">{formatINR(d.reserved)}</span></div>
        {d.held > 0 && <div className="mt-1 text-sm text-slate-400 font-mono flex items-center gap-2" data-testid="user-held"><Lock className="h-3.5 w-3.5 text-rose-400" /> On hold (dispute): <Link to="/dashboard/disputes" className="text-rose-300 underline">{formatINR(d.held)}</Link></div>}
        <div className="mt-6 flex flex-wrap gap-3">
          <Link to="/dashboard/deposit"><Button className="bg-emerald-500 hover:bg-emerald-400 text-[#08120e] font-semibold" data-testid="quick-deposit"><ArrowDownToLine className="mr-2 h-4 w-4" />Deposit</Button></Link>
          <Link to="/dashboard/withdraw"><Button variant="outline" className="border-slate-700 bg-white/5 hover:bg-white/10" data-testid="quick-withdraw"><ArrowUpFromLine className="mr-2 h-4 w-4" />Withdraw</Button></Link>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="ud-total-dep" icon={TrendingUp} label="Total Deposits" value={formatINR(d.total_deposit)} accent="emerald" />
        <StatCard testid="ud-total-wd" icon={TrendingDown} label="Total Withdrawals" value={formatINR(d.total_withdrawal)} accent="rose" />
        <StatCard testid="ud-pending-dep" icon={Clock} label="Pending Deposit" value={formatINR(d.pending_deposit_amount)} sub={`${d.pending_deposit_count} request(s)`} accent="amber" />
        <StatCard testid="ud-pending-wd" icon={Clock} label="Pending Withdrawal" value={formatINR(d.pending_withdrawal_amount)} sub={`${d.pending_withdrawal_count} request(s)`} accent="amber" />
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mt-4">
        <div className="vp-card rounded-xl p-5"><div className="text-xs font-mono uppercase tracking-widest text-slate-500">Deposit Limit</div><div className="mt-2 font-display text-xl font-bold">{formatINR(d.deposit_limit)}</div></div>
        <div className="vp-card rounded-xl p-5"><div className="text-xs font-mono uppercase tracking-widest text-slate-500">Withdrawal Limit</div><div className="mt-2 font-display text-xl font-bold">{formatINR(d.withdrawal_limit)}</div></div>
      </div>
    </div>
  );
}
